import { ChangeDetectionStrategy, Component, input, output, signal, viewChild } from '@angular/core';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { faCircleQuestion, faLightbulb } from '@fortawesome/pro-light-svg-icons';
import {
    AppNavigationActionComponent,
    AppNavigationBackButtonComponent,
    AppNavigationBodyComponent,
    AppNavigationComponent,
    AppNavigationFooterComponent,
    AppNavigationHeaderActionDirective,
    AppNavigationHeaderComponent,
    AppNavigationItemComponent,
    AppNavigationLogoCollapsedDirective,
    AppNavigationLogoDirective,
    AppNavigationNotificationsComponent,
    AppNavigationPopupContentDirective,
    AppNavigationPopupItemComponent,
    AppSwitcherApp,
    BadgeEmphasis,
    BadgeModule,
    IconComponent,
    OrgSwitcherComponent,
    SelectionMenuModule,
    SeparatorComponent,
    TagComponent,
    TooltipDirective,
} from '@pure/hive';
import { NotificationPopupComponent } from '@pure/notification-center';
import {
    AppNavigationAppSwitcherComponent,
    AppNavigationUserProfileComponent,
    PlatformPageLayoutComponent,
} from '@pure/pure1-ui-platform-angular';
import { ampli } from '../../../ampli';
import { PRODUCT_NAMES } from '../../../constants/product-names';
import { FusionStatusMenuItemComponent } from '../../../fusion-fleet/fusion-status-menu-item/fusion-status-menu-item.component';
import { MENU_SEPARATOR_TITLE } from '../../../services/tabs/tabs.model';
import { IMainTab } from '../../../services/tabs/tabs.service';
import { PluralizePipe } from '../../../ui/pipes/pluralize.pipe';
import { NavMenuIdPipe } from '../../pipes/nav-menu-id.pipe';
import { OrgSwitcherDirective } from '../org-switcher/org-switcher.directive';

export interface IConnectedCount {
    connected: number;
    disconnected: number;
}

/**
 * The app shell's Hive 7 rail, split out of `PageWrapperComponent` so it is checked only when one of
 * its inputs changes, an event fires inside it, or one of its own observables emits. The shell uses
 * default change detection and its template runs on every tick of every page; with the rail inline,
 * every rail item's host bindings and the org switcher's lists ran with it.
 *
 * It renders `platform-page-layout` as well as the rail, and the page content comes in through
 * `<ng-content />`. A wrapper element around `hive-app-navigation` alone would sit between it and
 * `.hive-app-layout`: the layout's `select="hive-app-navigation"` slot would miss it, and Hive sets
 * `--hive-layout-content-inset` with `.hive-app-layout:has(> .hive-app-navigation)`, which a wrapper
 * breaks even with `display: contents`. Projected page content still belongs to the shell's view, so
 * it is checked with the shell exactly as before.
 *
 * Everything the rail shows arrives as an input. The shell evaluates those bindings on every tick
 * (cheap property reads) and this component re-renders only when one of them changes, so a value the
 * shell mutates in place instead of replacing (an array or object input) would not reach the rail.
 */
@Component({
    selector: 'app-rail',
    templateUrl: './app-rail.component.html',
    styleUrl: './app-rail.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    imports: [
        PlatformPageLayoutComponent,
        AppNavigationComponent,
        AppNavigationHeaderComponent,
        AppNavigationBodyComponent,
        AppNavigationFooterComponent,
        AppNavigationItemComponent,
        AppNavigationLogoDirective,
        AppNavigationLogoCollapsedDirective,
        AppNavigationHeaderActionDirective,
        AppNavigationActionComponent,
        AppNavigationNotificationsComponent,
        AppNavigationBackButtonComponent,
        AppNavigationPopupContentDirective,
        AppNavigationPopupItemComponent,
        AppNavigationUserProfileComponent,
        AppNavigationAppSwitcherComponent,
        IconComponent,
        OrgSwitcherComponent,
        OrgSwitcherDirective,
        SelectionMenuModule,
        FusionStatusMenuItemComponent,
        TagComponent,
        BadgeModule,
        TooltipDirective,
        RouterLink,
        SeparatorComponent,
        NgTemplateOutlet,
        NotificationPopupComponent,
        AsyncPipe,
        PluralizePipe,
        NavMenuIdPipe,
    ],
})
export class AppRailComponent {
    // Menu and active item
    readonly tabs = input<IMainTab[]>([]);
    readonly activeItemId = input<string>();
    readonly activeNavigation = input<'pure1' | 'draas'>('pure1');

    // Header
    readonly showRecommendations = input(false);
    readonly recommendationCount = input<number>();
    readonly notificationCount = input<number | null>(null);
    readonly showOrgSwitcher = input(false);
    readonly isOrgSwitcherReadonly = input(false);

    // Item badges and tags
    readonly isVmAssessmentsBetaEnabled = input(false);
    readonly isEOSCEnabled = input(false);
    readonly showEolApplianceBadge = input(false);
    readonly eolApplianceCount = input<number>();
    readonly showEolOrEoscApplianceBadge = input(false);
    readonly eolOrEoscApplianceCount = input<number>();
    readonly eolOrEoscApplianceTooltip = input<string>();
    readonly expiredSupportContractCount = input<number>();
    readonly showArrayCountBadge = input(false);
    readonly arrayCounts = input<IConnectedCount>();
    readonly openAlertsCount = input(0);
    readonly alertsEmphasisBadge = input<BadgeEmphasis>();
    readonly openCasesCount = input(0);

    // Footer
    readonly pageHelpUrl = input<string>();
    readonly canReadSupportKnowledge = input(false);
    readonly canReadSupportCommunity = input(false);
    readonly communityUrl = input<string>();

    readonly activeItemIdChange = output<string>();
    readonly collapseToggle = output<boolean>();
    readonly recommendationsClick = output<void>();
    readonly backClick = output<void>();
    readonly orgSwitchError = output<{ message: string; cause: unknown }>();
    readonly helpItemClick = output<string>();
    readonly appSwitcherVisibilityChange = output<boolean>();
    readonly appSwitcherAppClick = output<AppSwitcherApp>();

    /** Which sections are expanded. Nothing outside the rail reads it; Hive owns the state. */
    protected readonly expandedItemIds = signal<string[]>([]);
    /**
     * Whether the org switcher's menu items exist. A user can belong to hundreds of orgs, and every item
     * would be rendered on load and re-checked on every check of the rail. The items render when the
     * pointer or focus reaches the switcher, which is before its popup opens, and go away when the
     * popup closes.
     */
    protected readonly orgListRendered = signal(false);

    private readonly navigation = viewChild(AppNavigationComponent);

    protected readonly MENU_SEPARATOR_TITLE = MENU_SEPARATOR_TITLE;
    protected readonly PRODUCT_NAMES = PRODUCT_NAMES;
    protected readonly ampli = ampli;
    protected readonly faLightbulb = faLightbulb;
    protected readonly faCircleQuestion = faCircleQuestion;

    /**
     * Puts Hive's active item back on `id`. A click sets Hive's active item before a guard can cancel the
     * navigation, and the `activeItemId` binding cannot see the reset because its value never changed.
     */
    resetActiveItem(id: string): void {
        this.navigation()?.activeItemId.set(id);
    }
}
